import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, List, ListItem, ListItemIcon, ListItemText,
  CircularProgress, Tooltip, Chip, Snackbar, Alert,
} from '@mui/material';
import {
  AttachFile, UploadFile, Download, DeleteOutline, InsertDriveFile, PictureAsPdf, Image as ImageIcon,
} from '@mui/icons-material';
import { documentsService, DocMeta } from '../../services/documentsService';

/**
 * Reusable attachments panel — drops onto any transaction detail page to give it
 * document support over the generic `documents` store. Pass the entity type/id
 * and it lists, uploads, downloads and deletes files scoped to that record.
 */

const fmtSize = (b: number | null) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (mime: string | null, name: string) => {
  const m = (mime || '').toLowerCase();
  const n = (name || '').toLowerCase();
  if (m.includes('pdf') || n.endsWith('.pdf')) return <PictureAsPdf fontSize="small" color="error" />;
  if (m.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(n)) return <ImageIcon fontSize="small" color="primary" />;
  return <InsertDriveFile fontSize="small" color="action" />;
};

const AttachmentsPanel: React.FC<{
  entityType: string;
  entityId: number | string | undefined | null;
  title?: string;
  dense?: boolean;
}> = ({ entityType, entityId, title = 'Documents', dense }) => {
  const [docs, setDocs] = useState<DocMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    if (!entityId) { setDocs([]); setLoading(false); return; }
    setLoading(true);
    documentsService.list(entityType, entityId)
      .then((r) => setDocs(r.documents || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  useEffect(load, [load]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !entityId) return;
    setBusy(true);
    try {
      await documentsService.upload(file, entityType, entityId);
      setToast({ msg: `Uploaded ${file.name}`, sev: 'success' });
      load();
    } catch (err: any) {
      setToast({ msg: err.message || 'Upload failed', sev: 'error' });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onDownload = async (d: DocMeta) => {
    try { await documentsService.download(d.id, d.file_name); }
    catch (err: any) { setToast({ msg: err.message || 'Download failed', sev: 'error' }); }
  };

  const onDelete = async (d: DocMeta) => {
    setBusy(true);
    try { await documentsService.remove(d.id); setToast({ msg: 'Deleted', sev: 'success' }); load(); }
    catch (err: any) { setToast({ msg: err.message || 'Delete failed', sev: 'error' }); }
    finally { setBusy(false); }
  };

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachFile fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
          <Chip size="small" label={docs.length} />
        </Box>
        <Button size="small" variant="outlined" startIcon={busy ? <CircularProgress size={14} /> : <UploadFile />} disabled={busy || !entityId} onClick={() => inputRef.current?.click()}>
          Upload
        </Button>
        <input ref={inputRef} type="file" hidden onChange={onPick} />
      </Box>

      {loading ? (
        <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>
      ) : !docs.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
          {entityId ? 'No documents attached yet.' : 'Save the record to attach documents.'}
        </Typography>
      ) : (
        <List dense={dense} disablePadding>
          {docs.map((d) => (
            <ListItem
              key={d.id}
              secondaryAction={
                <Box>
                  <Tooltip title="Download"><IconButton edge="end" size="small" onClick={() => onDownload(d)}><Download fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton edge="end" size="small" onClick={() => onDelete(d)} disabled={busy}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                </Box>
              }
              sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>{iconFor(d.mime_type, d.file_name)}</ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" sx={{ fontWeight: 500, pr: 6 }}>{d.file_name}</Typography>}
                secondary={`${fmtSize(d.size_bytes)}${d.created_at ? ` · ${String(d.created_at).slice(0, 10)}` : ''}`}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Paper>
  );
};

export default AttachmentsPanel;
